import { drawsAt, formatAngle, formatLength, localGeometryBounds, snapPoint, type Rect } from '@flobi/artwork-core';
import type { IconName } from '../chrome/icons';
import type { Overlay, Point, RailButton, Tool, ToolContext, Well } from './types';

/**
 * The Measure tool: how far, how wide, at what angle.
 *
 * Every other tool here changes the drawing. This one asks it a question, which makes it
 * the odd one out and is exactly why it has to be careful: a measurement that is out by
 * a pixel because the hand was out by a pixel is not a measurement, it is a guess with a
 * number on it. So it snaps — to corners, to middles, to centres — and says what it
 * snapped to by drawing the mark there. A number on screen means the tool found that
 * point, not that the hand happened to land near it.
 *
 * Three questions, which is what Illustrator's Dimension tool means by linear, angular
 * and radial:
 *
 *   — between two places, which also gives the across and the down and the angle;
 *   — between two lines from a corner, which is the angle itself;
 *   — round a circle, which is its radius and its diameter.
 *
 * It draws nothing into the document. What it reports is gone when you measure something
 * else, because a measurement is a question and the answer belongs to the moment you
 * asked it.
 */

type Mode = 'linear' | 'angular' | 'radial';

const MODES: { id: Mode; icon: IconName; title: string }[] = [
  { id: 'linear', icon: 'dimension', title: 'Linear — drag between two places for the distance, the across and the down' },
  { id: 'angular', icon: 'dimension-angle', title: 'Angular — drag one arm from the corner, then click where the other goes' },
  { id: 'radial', icon: 'dimension-radius', title: 'Radial — click a round shape for its radius and diameter' },
];

/** How near a corner or a middle counts as being on it, in screen pixels. */
const CATCH = 9;
/** How far around the pointer to look for something to catch on, in screen pixels. */
const LOOK = 60;
/** Shorter than this and it was a click, not a measurement. */
const LEAST = 3;

export function measureTool(): Tool {
  let mode: Mode = 'linear';
  /** The line being dragged, or the last one measured. */
  let line: { from: Point; to: Point } | null = null;
  /** The second arm of an angle, once the first has been drawn. */
  let arm: Point | null = null;
  let radial: { centre: Point; radius: number } | null = null;
  let drawing = false;
  /** Where the pointer caught, so the mark is drawn where the number came from. */
  let caught: Point | null = null;

  /**
   * The places worth measuring to, around a point.
   *
   * Corners, edge middles and the centre of everything near: those are the places a
   * drawing is actually built out of, and the ones somebody means when they put the
   * pointer "on" a shape.
   */
  const placesNear = (ctx: ToolContext, world: Point): Point[] => {
    const look = LOOK * ctx.unitsPerPixel;
    const around: Rect = { x: world.x - look, y: world.y - look, width: look * 2, height: look * 2 };
    const out: Point[] = [];
    for (const id of ctx.nearby(around)) {
      if (ctx.node(id)?.type === 'slice') continue;
      const box = ctx.boundsOf(id);
      if (!box) continue;
      const midX = box.x + box.width / 2;
      const midY = box.y + box.height / 2;
      const right = box.x + box.width;
      const bottom = box.y + box.height;
      out.push(
        { x: box.x, y: box.y },
        { x: right, y: box.y },
        { x: right, y: bottom },
        { x: box.x, y: bottom },
        { x: midX, y: box.y },
        { x: right, y: midY },
        { x: midX, y: bottom },
        { x: box.x, y: midY },
        { x: midX, y: midY },
      );
    }
    return out;
  };

  /** A point pulled onto the nearest place worth measuring to, if there is one near. */
  const settle = (ctx: ToolContext, world: Point): { at: Point; caught: boolean } => {
    const snap = snapPoint(world, placesNear(ctx, world), CATCH * ctx.unitsPerPixel);
    const moved = snap.dx !== 0 || snap.dy !== 0;
    return { at: { x: world.x + snap.dx, y: world.y + snap.dy }, caught: moved };
  };

  /** Held straight: the eight directions a measurement is usually taken along. */
  const straightened = (from: Point, to: Point): Point => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const step = Math.PI / 4;
    const angle = Math.round(Math.atan2(dy, dx) / step) * step;
    const length = Math.hypot(dx, dy);
    return { x: from.x + Math.cos(angle) * length, y: from.y + Math.sin(angle) * length };
  };

  const forget = () => {
    line = null;
    arm = null;
    radial = null;
    drawing = false;
    caught = null;
  };

  /** The angle at a corner between two arms, in degrees, the short way round. */
  const angleAt = (vertex: Point, a: Point, b: Point): number => {
    const one = Math.atan2(a.y - vertex.y, a.x - vertex.x);
    const two = Math.atan2(b.y - vertex.y, b.x - vertex.x);
    let turn = ((two - one) * 180) / Math.PI;
    while (turn <= -180) turn += 360;
    while (turn > 180) turn -= 360;
    return Math.abs(turn);
  };

  return {
    id: 'measure',

    activate(ctx) {
      forget();
      ctx.setCursor('crosshair');
      ctx.refresh();
    },

    deactivate(ctx) {
      forget();
      ctx.setCursor(null);
      ctx.refresh();
    },

    onPointerDown(_event, world, ctx) {
      const found = settle(ctx, world);
      if (mode === 'radial') {
        // The shape itself, not the group it sits in: a group's box gave a meaningless
        // radius (QA 2026-09-24, cut-slice-measure-F25).
        let id = ctx.hit(world);
        const slop = CATCH * ctx.unitsPerPixel;
        while (id && ctx.node(id)?.type === 'group') {
          const kids = ctx.scene.childrenOf(id);
          let inner: string | null = null;
          for (let i = kids.length - 1; i >= 0 && !inner; i--) if (!ctx.scene.hiddenHere(kids[i]!) && drawsAt(ctx.scene, kids[i]!, world, slop)) inner = kids[i]!;
          if (!inner) break;
          id = inner;
        }
        const node = id ? ctx.node(id) : undefined;
        const local = node ? localGeometryBounds(node) : null;
        // Half the shorter side of the shape's OWN box, measured on the page: for a circle
        // that is its radius, and for anything else the biggest circle that fits. The
        // page-aligned box of a rotated ellipse is bigger than the ellipse, and gave r 106
        // for one 100 across (QA 2026-09-24, cut-slice-measure-F25).
        if (id && local) {
          const m = ctx.scene.worldTransform(id);
          const across = local.width * Math.hypot(m[0], m[1]);
          const down = local.height * Math.hypot(m[2], m[3]);
          const cx = local.x + local.width / 2;
          const cy = local.y + local.height / 2;
          radial = { centre: { x: m[0] * cx + m[2] * cy + m[4], y: m[1] * cx + m[3] * cy + m[5] }, radius: Math.min(across, down) / 2 };
        } else {
          const box = id ? ctx.boundsOf(id) : null;
          radial = box ? { centre: { x: box.x + box.width / 2, y: box.y + box.height / 2 }, radius: Math.min(box.width, box.height) / 2 } : null;
        }
        line = null;
        ctx.refresh();
        return true;
      }
      // The second arm of an angle is a click, not a drag: the corner and the first arm
      // are already placed, and all that is left to say is which way the other one goes.
      if (mode === 'angular' && line && !arm) {
        arm = found.at;
        caught = found.caught ? found.at : null;
        ctx.refresh();
        return true;
      }
      arm = null;
      radial = null;
      drawing = true;
      line = { from: found.at, to: found.at };
      caught = found.caught ? found.at : null;
      ctx.refresh();
      return true;
    },

    onPointerMove(event, world, ctx) {
      const found = settle(ctx, world);
      caught = found.caught ? found.at : null;
      // On every move, not only when picked up: crossing a ruler set the arrow back and it
      // stayed (QA 2026-09-24, cut-slice-measure-F28).
      ctx.setCursor('crosshair');
      if (!drawing || !line) {
        // The catch mark is shown before anything is pressed, so it is clear what a press
        // would measure FROM rather than only afterwards.
        ctx.refresh();
        return;
      }
      line = { from: line.from, to: event.shiftKey ? straightened(line.from, found.at) : found.at };
      ctx.refresh();
    },

    onPointerUp(event, world, ctx) {
      if (!drawing || !line) return;
      drawing = false;
      const found = settle(ctx, world);
      const to = event.shiftKey ? straightened(line.from, found.at) : found.at;
      // A press that went nowhere was a press, and leaving a zero-length measurement on
      // screen says the tool did something when it did not.
      if (Math.hypot(to.x - line.from.x, to.y - line.from.y) < LEAST * ctx.unitsPerPixel) line = null;
      else line = { from: line.from, to };
      ctx.refresh();
    },

    onKeyDown(event, ctx) {
      if (event.key !== 'Escape') return false;
      if (!line && !radial) return false;
      event.preventDefault();
      forget();
      ctx.refresh();
      return true;
    },

    rail(ctx) {
      const items: RailButton[] = MODES.map((item) => ({
        id: item.id,
        icon: item.icon,
        title: item.title,
        pressed: mode === item.id,
        run: () => {
          mode = item.id;
          forget();
          ctx.railChanged();
          ctx.refresh();
        },
      }));
      return { top: { kind: 'buttons' as const, items } as Well };
    },

    draw(overlay: Overlay, ctx: ToolContext) {
      const unit = ctx.units;
      const per = ctx.unitsPerPixel;
      const tick = 5 * per;

      if (caught) overlay.handle(caught, { filled: true, size: 3.5 });

      if (radial) {
        const edge = { x: radial.centre.x + radial.radius, y: radial.centre.y };
        overlay.line(radial.centre, edge, { width: 1 });
        overlay.handle(radial.centre, { filled: true, size: 3 });
        overlay.label(
          { x: radial.centre.x + radial.radius / 2, y: radial.centre.y },
          `r ${formatLength(radial.radius, unit)}`,
          { align: 'center', baseline: 'middle', offset: { x: 0, y: -10 } },
        );
        overlay.label(
          { x: radial.centre.x + radial.radius / 2, y: radial.centre.y },
          `⌀ ${formatLength(radial.radius * 2, unit)}`,
          { align: 'center', baseline: 'middle', offset: { x: 0, y: 10 } },
        );
        return;
      }

      if (!line) return;
      const { from, to } = line;
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const length = Math.hypot(dx, dy);

      overlay.line(from, to, { width: 1.5 });
      // Ticks across the ends, so the line reads as a measurement between two places
      // rather than as something drawn.
      const nx = length > 0 ? -dy / length : 0;
      const ny = length > 0 ? dx / length : 0;
      for (const end of [from, to]) {
        overlay.line({ x: end.x - nx * tick, y: end.y - ny * tick }, { x: end.x + nx * tick, y: end.y + ny * tick }, { width: 1.5 });
      }

      const middle = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
      if (mode === 'angular' && arm) {
        const turn = angleAt(from, to, arm);
        overlay.line(from, arm, { width: 1.5 });
        for (const end of [arm]) {
          const armLength = Math.hypot(arm.x - from.x, arm.y - from.y) || 1;
          const ax = -(arm.y - from.y) / armLength;
          const ay = (arm.x - from.x) / armLength;
          overlay.line({ x: end.x - ax * tick, y: end.y - ay * tick }, { x: end.x + ax * tick, y: end.y + ay * tick }, { width: 1.5 });
        }
        overlay.label(from, formatAngle(turn), { align: 'center', baseline: 'middle', offset: { x: 0, y: -16 } });
        overlay.label(middle, formatLength(length, unit), { align: 'center', baseline: 'middle', offset: { x: 0, y: -10 }, tone: 'quiet' });
        return;
      }

      // How far, and then the across and the down under it: the three numbers a person
      // measuring almost always wants together, because a distance on its own does not
      // tell you how to move anything.
      overlay.label(middle, formatLength(length, unit), { align: 'center', baseline: 'middle', offset: { x: 0, y: -12 } });
      overlay.label(
        middle,
        `${formatLength(Math.abs(dx), unit, { bare: true })} × ${formatLength(Math.abs(dy), unit)}   ${formatAngle(-(Math.atan2(dy, dx) * 180) / Math.PI)}`,
        { align: 'center', baseline: 'middle', offset: { x: 0, y: 12 }, tone: 'quiet' },
      );
      if (mode === 'angular' && !arm) {
        overlay.label(to, 'click where the other arm goes', { align: 'left', baseline: 'middle', offset: { x: 12, y: 0 }, tone: 'quiet' });
      }
    },
  };
}
